import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import asm from 'assembly/Asm';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import * as biojs from 'BioJS';
import {AnnotationAttributes} from 'biojs/Annotation';
import {NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE, NucleotideType} from 'biojs/BioConstants';
import {PartAttributes} from 'biojs/Part';
import {ModifiedNucleotides, TranslationGroup} from 'biojs/Sequence';
import {SequencePrimerAttributes} from 'biojs/SequencePrimer';
import {TranslationAttributes} from 'biojs/Translation';
import {dnaToRNA, revcomp, rnaToDNA} from 'biojs/Util';
import {DoSeqOpReturnPromise, Sequence as ComponentSequence} from 'components/Sequence';
import {Side} from 'constants/MolbioConstants';
import {ModifiedNucleotideAttributes, SequenceType} from 'constants/SequenceConstants';
import {CopyPreferences, PolymerFeatureAttributes} from 'constants/SequenceConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import {
  getModsForUTFlexibleNaturalBasesIfEnabled,
  getUTFlexibleNaturalBasesIfEnabled,
  lockImplicitMods,
} from 'modbio/util';
import {AminoAcidLayout} from 'molbio/aa-sequences/AminoAcids';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import {
  CopyNucleotideSequenceMode,
  getCopyDataForSequenceAndSelection,
  getPrimersForCopy,
  NucleotideTargetCopyMode,
  doesCopyMatchNucleotideType,
} from 'molbio/CopyUtil';
import * as EnzymeUtil from 'molbio/enzymes/EnzymeUtil';
import {INPUT_BUBBLE_OPERATION} from 'molbio/maps/InputBubble';
import InputBubbleView from 'molbio/maps/linear/InputBubbleView';
import {NonEmptySelection, Selection} from 'molbio/maps/selectables/Selection';
import Type from 'molbio/maps/Type';
import {replaceNaturalSugarMonomersForModifiedNucleotides} from 'molbio/monomers/MonomersUtil';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import PolymerMapTabState from 'molbio/tabstates/PolymerMapTabState';
import notifications from 'notifications';
import * as popup from 'Popup';
import preferences from 'Preferences';
import * as bclip from 'util/BenchlingClipboard';
import {ellipsize} from 'util/NameUtil';

import PolymerMapTabView from './PolymerMapTabView';

function aminoAcidBoxHeight(layout: AminoAcidLayout): number {
  return layout.vMargin + new Type(layout.type.fontSize, layout.type.letterSpacing).height();
}

function addPolymersForSearch(sequence: ComponentSequence): void {
  SearchDNAData.get().addSequences([sequence]);
}

function removePolymersForSearch(sequence: ComponentSequence): void {
  SearchDNAData.get().removeSequence(sequence);
}

export function shouldPreserveModsForCopyMode(
  source: ComponentSequence | SequencePrimer,
  copyMode: CopyNucleotideSequenceMode
): boolean {
  const couldUseOriginalMode = source.canUseFullUTFlexibility();
  const modsPreservingMode = couldUseOriginalMode
    ? CopyNucleotideSequenceMode.ORIGINAL
    : CopyNucleotideSequenceMode[source.nucleotideType()];
  return copyMode === modsPreservingMode;
}

/**
 * Handles logic for storing a subregion of a sequence or an entire sequence primer in the Benchling Clipboard (BC)
 * when copying as a nucleotide sequence. Not applicable when copying the source as an AA Translation. Responsible
 * for copying sequence features and does not require that the copied region is selected (which can be useful for cases
 * like the legacy oligo view).
 *
 * Returns a hash that can be used to retrieve the BC metadata.
 * @param source - the copy source, either a component sequence or component sequence primer
 * @param copyMode - the style of copy being made
 * @param includeSequenceFeatures - whether to include sequence features like AA Translations, Annotations, and Parts
 * @param includeSequenceFeaturesNotFullyContained - whether to include sequence features not fully contained in the copy region
 * @param sourceStart - start of the copy region from the perspective of the source
 * @param sourceEnd - end of the copy region from the perspective of the source
 * @param selection - optional Selection component used for storing selected Enzymes. Does not affect the copy region
 * @param copyDescription - optional description for the clipboard store
 * @returns clipboard hash corresponding to the BC store event
 */
function storeNucleotideRegionInBenchlingClipboard(
  source: ComponentSequence | SequencePrimer,
  copyMode: NucleotideTargetCopyMode,
  includeSequenceFeatures: boolean,
  includeSequenceFeaturesNotFullyContained: boolean,
  sourceStart: number,
  sourceEnd: number,
  selection?: NonEmptySelection,
  copyDescription?: string
): string {
  const copyModeIsReverseComplement = [
    CopyNucleotideSequenceMode.DNA_REVERSE_COMPLEMENT,
    CopyNucleotideSequenceMode.RNA_REVERSE_COMPLEMENT,
  ].includes(copyMode);
  const hashDescription =
    selection && !copyModeIsReverseComplement
      ? bclip.clipboard.stdDesc(selection)
      : bclip.clipboard.defaultDesc();
  const clipboardMeta: bclip.ClipboardMeta = {
    'copied-sourceNucleotideType': source.nucleotideType(),
  };
  let bases: string;

  // Store enzymes unconditionally.
  selection?.storeEnzymes(clipboardMeta);

  if (source instanceof ComponentSequence) {
    const newSequence = source.createFromSelection(sourceStart, sourceEnd, {
      annotations: true, // Always include annotations to get modified nucleotides.
      notcontained: includeSequenceFeaturesNotFullyContained,
      reverseComplement: copyModeIsReverseComplement,
      primers: getPrimersForCopy(source),
      save: false,
    });
    bases = newSequence.bases();
    clipboardMeta['copied-modified-nucleotides'] = newSequence.modifiedNucleotides();
    if (includeSequenceFeatures) {
      clipboardMeta['copied-annotations'] = newSequence.annotations();
      clipboardMeta['copied-translations'] = newSequence.translations();
      clipboardMeta['copied-translation-groups'] = newSequence.translationGroups();
      clipboardMeta['copied-sequencePrimers'] = newSequence.sequencePrimers();
      clipboardMeta['copied-parts'] = newSequence.parts();
    }
  } else {
    // The source is a primer, use the entire contents.
    bases = source.bases();
    clipboardMeta['copied-modified-nucleotides'] = source.modifiedNucleotides();
    if (copyModeIsReverseComplement) {
      bases = revcomp(bases, NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[source.nucleotideType()]);
      // Cannot know revcomp of mods, so drop them.
      delete clipboardMeta['copied-modified-nucleotides'];
    }
  }

  if (source.canUseFullUTFlexibility() && copyMode === CopyNucleotideSequenceMode.ORIGINAL) {
    bases = getUTFlexibleNaturalBasesIfEnabled(
      bases,
      clipboardMeta['copied-modified-nucleotides'],
      source.nucleotideType(),
      // canUseFullUTFlexibility truthiness implies that the entity is an oligo.
      SequenceType.OLIGO
    );
  }

  if (!doesCopyMatchNucleotideType(source.nucleotideType(), copyMode)) {
    // Never copy parts across nucleotide type.
    delete clipboardMeta['copied-parts'];
    if (source.nucleotideType() === NucleotideType.RNA) {
      // Source is RNA and target is DNA.
      bases = rnaToDNA(bases);
    } else {
      // Source is DNA and target is RNA.
      bases = dnaToRNA(bases);
    }
  }

  if (!shouldPreserveModsForCopyMode(source, copyMode)) {
    delete clipboardMeta['copied-modified-nucleotides'];
  }

  const hash = bclip.clipboard.getHash(bases, hashDescription);
  bclip.clipboard.store(bases, clipboardMeta, hashDescription, copyDescription);
  return hash;
}

function maybeStoreNucleotideRegionInBenchlingClipboard(inputSelection: Selection, e?: Event): string | null {
  if (!inputSelection.isNotEmpty()) {
    return null;
  }
  const {start, end, selection, source} = getCopyDataForSequenceAndSelection(
    inputSelection.sequence() as ComponentSequence,
    inputSelection
  );
  const copyMode = source.canUseFullUTFlexibility()
    ? CopyNucleotideSequenceMode.ORIGINAL
    : CopyNucleotideSequenceMode[source.nucleotideType()];
  // This function performs a simple copy of the forward strand of the source. The copy mode matches the nucleotide
  // type of the source since there is no "cross-copying" (i.e. copying from a DNA source as RNA or vice versa).
  return storeNucleotideRegionInBenchlingClipboard(
    source,
    copyMode,
    preferences.get(CopyPreferences.ANNOTATIONS),
    preferences.get(CopyPreferences.ANNOTATIONS_NOT_CONTAINED),
    start,
    end,
    selection
  );
}

export const INVALID_PASTE_MESSAGE = 'The clipboard cannot be pasted in as it contains invalid bases.';

function _warnOnInvalidNullPhosphate(
  sequence: ComponentSequence,
  copiedModifiedNucleotides: Readonly<Array<ModifiedNucleotideAttributes>>
): boolean {
  // Oligos handle null phosphates in the backend
  if (sequence.isOligo()) {
    return false;
  }
  const selection = sequence.get('selection');
  const invalid =
    // If there is a null phosphate monomer ID, assume it is the last monomer.
    copiedModifiedNucleotides.some((mod) => mod.phosphateMonomerId == null) &&
    // Enforce that it will be positioned at the very end of the target sequence after the paste operation.
    (selection.end() ?? selection.start()) !== sequence.length();

  if (invalid) {
    notifications.warn('Cannot create null internal phosphate');
  }
  return invalid;
}

function handlePasteRawText(sequence: ComponentSequence, text: string): void {
  const acceptMixedBases = sequence.canUseFullUTFlexibility();
  const sanitizedBases = biojs.util.sanitize(text, sequence.nucleotideType(), acceptMixedBases);
  if (sanitizedBases.length === 0) {
    notifications.warn('No valid bases were provided to paste.');
    return;
  }

  if (sanitizedBases !== text.replace(/\s/g, '')) {
    notifications.info(
      <>
        The bases pasted were not all valid, and were sanitized from <strong>{ellipsize(text, 20)}</strong> to{' '}
        <strong>{ellipsize(sanitizedBases, 20)}</strong>
      </>
    );
  }

  const modifiedNucleotides = getModsForUTFlexibleNaturalBasesIfEnabled(
    sanitizedBases,
    sequence.nucleotideType(),
    sequence.type()
  );

  // Coerce bases to the expected nucleotide type before handing off to seqops
  const finalBases = biojs.util.sanitize(sanitizedBases, sequence.nucleotideType(), false);
  paste(sequence, [], finalBases, [], [], [], [], modifiedNucleotides);
}

function handlePasteMixedBases(
  sequence: ComponentSequence,
  bases: string,
  copiedModifiedNucleotides: ModifiedNucleotides,
  annotations: Array<AnnotationAttributes>,
  sourceNucleotideType: NucleotideType
): void {
  if (!biojs.util.isValidMixedBaseSequence(bases)) {
    notifications.error(INVALID_PASTE_MESSAGE);
    return;
  }
  if (_warnOnInvalidNullPhosphate(sequence, copiedModifiedNucleotides)) {
    return;
  }

  // We don't need to generate U/T mods separately here because lockImplicitMods will always emit a mod
  // dict for every base
  const newMods = lockImplicitMods(
    bases,
    copiedModifiedNucleotides,
    sourceNucleotideType,
    sequence.nucleotideType()
  );

  // Coerce bases to the expected nucleotide type before handing off to seqops
  const finalBases = biojs.util.sanitize(bases, sequence.nucleotideType(), false);
  paste(sequence, [], finalBases, annotations, [], [], [], newMods);
}

async function handlePasteText(sequence: ComponentSequence, text: string): Promise<void> {
  if (!sequence.allowResidueEdits()) {
    return;
  }

  const selection = sequence.get('selection');
  const _sequence = sequence.get('_sequence');
  // Look up text in clipboard
  const data = bclip.clipboard.grab(text);
  if (data.state === bclip.ClipboardGrabState.RAW) {
    handlePasteRawText(sequence, text);
    return;
  }

  const copiedAnnotations = data.meta['copied-annotations'] ?? [];
  const copiedTranslations = data.meta['copied-translations'] ?? [];
  const copiedTranslationGroups = data.meta['copied-translation-groups'] ?? [];
  const copiedSequencePrimers = data.meta['copied-sequencePrimers'] ?? [];
  const copiedParts = data.meta['copied-parts'] ?? [];
  const copiedModifiedNucleotides = data.meta['copied-modified-nucleotides'] ?? [];
  const sourceNucleotideType = data.meta['copied-sourceNucleotideType'];

  // Skip fallback value for copied-enzymes since it only makes sense for the value
  // to be a two-element array and the existing logic relies on checking whether
  // copiedEnzymes is falsey, which is not the case for empty arrays.
  const copiedEnzymes = data.meta['copied-enzymes'];
  const copiedBases = data.text;
  // Refuse to paste empty bases.
  if (copiedBases.length === 0) {
    return;
  }

  const isSingleStranded = sequence.isSingleStranded();
  const isRNASequence = sequence.isRNASequence();
  const targetNucleotideType = sequence.nucleotideType();

  const annotations: Array<AnnotationAttributes> = sanitizeFeatures({
    sequence,
    selection,
    features: copiedAnnotations,
    featureType: SanitizeFeatureType.ANNOTATIONS,
    rejectReverseStrand: isSingleStranded,
  });

  if (sequence.canUseFullUTFlexibility()) {
    handlePasteMixedBases(
      sequence,
      copiedBases,
      copiedModifiedNucleotides,
      annotations,
      sourceNucleotideType!
    );
    return;
  }

  if (!biojs.util.isValidSequence(copiedBases, _sequence.nucleotideType())) {
    // Ensure the bases in our clipboard are valid for the sequence being pasted into.
    // Prevents pasting '-' from an alignment into a normal sequence.
    notifications.error(INVALID_PASTE_MESSAGE);
    return;
  }

  let modifiedNucleotides: Array<ModifiedNucleotideAttributes> = [];
  if (sequence.supportsChemicalMods()) {
    if (_warnOnInvalidNullPhosphate(sequence, copiedModifiedNucleotides)) {
      return;
    }
    modifiedNucleotides = await replaceNaturalSugarMonomersForModifiedNucleotides(
      copiedModifiedNucleotides,
      sourceNucleotideType!,
      sequence.nucleotideType()
    );
  }

  if (sequence.isOligo()) {
    // If pasting into an oligo, ignore translations, primers, and parts but include modified nucleotides and
    // annotations. Note that invalid base modifications will be caught in `isValidSequence`, so we can assume
    // that all of the modifications are valid for the target oligo at this point.
    paste(sequence, [], copiedBases, annotations, [], [], [], modifiedNucleotides);
    return;
  }

  let translations = copiedTranslations;
  let sequencePrimers = copiedSequencePrimers;
  let parts = copiedParts;
  let bases = copiedBases;

  translations = sanitizeFeatures({
    sequence,
    selection,
    features: translations,
    featureType: SanitizeFeatureType.TRANSLATIONS,
    rejectReverseStrand: isSingleStranded,
  });
  // Don't reject reverse primers for sequence primer even if it's an RNA sequence;
  // we allow reverse primers there
  sequencePrimers = sanitizeFeatures({
    sequence,
    selection,
    features: sequencePrimers,
    featureType: SanitizeFeatureType.SEQUENCE_PRIMERS,
    rejectReverseStrand: false,
  });
  // If the clipboard metadata is nullish, it's because the source of the copy is from outside the app.
  // As such, annotations, translations, sequencePrimers, and parts will only be defined and non-empty
  // when sourceNucleotideType is also defined.
  parts = sanitizeParts(sequence, selection, parts, targetNucleotideType, sourceNucleotideType!);

  if (copiedEnzymes) {
    let matched = false;
    if (selection.canDigest()) {
      const enzymeIds: [number, number] = [
        selection.getStartState('cutsite')!.get('enzyme').id,
        selection.getEndState('cutsite')!.get('enzyme').id,
      ];

      const singleMatches = (id: number, copied: number): boolean =>
        EnzymeUtil.getCompatibleEnzymeIds(copied).includes(id);

      const matches = (ids: [number, number], copiedIds: [number, number]): boolean =>
        isEqual(ids, copiedIds) ||
        (singleMatches(ids[0], copiedIds[0]) && singleMatches(ids[1], copiedIds[1]));
      if (matches(enzymeIds, copiedEnzymes)) {
        matched = true;
      } else if (matches(enzymeIds, copiedEnzymes.reverse() as [number, number])) {
        bases = biojs.util.revcomp(bases, _sequence.baseTable());
        biojs.util.revcompParts(bases.length, parts);
        biojs.util.revcompFeatures(bases.length, annotations);
        biojs.util.revcompFeatures(bases.length, translations);

        const enzymes = sequence.get('enzymes');

        const changeSelection = (field: Side, index: number): void => {
          const enzyme = enzymes.get(enzymeIds[index]);
          const offset: number = enzyme.get('offset');
          // canDigest returning true indicates that the selection is not empty.
          let current = (selection as NonEmptySelection).get(field) + offset;
          const length = sequence.length();
          current = ((current % length) + length) % length;
          selection.set(field, current);
        };

        changeSelection('start', 0);
        changeSelection('end', 1);
        matched = true;
      }

      if (sequence.length() !== 0 && !matched) {
        const msg =
          'The copied selection had two flanking cut sites that do not match the pasted region. ' +
          'Are you sure you want to continue pasting?';

        new popup.Confirm({title: 'Mismatched Cut Sites', msg})
          .choice('OK', 'btn-af-primary', () => {
            paste(
              sequence,
              parts,
              bases,
              annotations,
              translations,
              copiedTranslationGroups,
              sequencePrimers,
              isRNASequence ? modifiedNucleotides : []
            );
          })
          .choice('Cancel', 'btn-af-default')
          .show();
        return;
      }
    }
  }

  paste(
    sequence,
    parts,
    bases,
    annotations,
    translations,
    copiedTranslationGroups,
    sequencePrimers,
    isRNASequence ? modifiedNucleotides : []
  );
}

function paste(
  sequence: ComponentSequence,
  parts: Array<PartAttributes>,
  bases: string,
  annotations: Array<AnnotationAttributes>,
  translations: Array<TranslationAttributes>,
  translationGroups: Array<TranslationGroup>,
  sequencePrimers: Array<SequencePrimerAttributes>,
  modifiedNucleotides: ModifiedNucleotides
): void {
  let deferred: DoSeqOpReturnPromise;
  let pos: number;
  let start: number;
  const _sequence = sequence.get('_sequence');
  const selection = sequence.get('selection');

  if (!asm.of(_sequence).canEditSelection(_sequence, selection, notifications.warn)) {
    return;
  }

  const writable = sequence.allowResidueEdits() && sequence.isSelectionEditable();
  const deletable = sequence.allowResidueEdits() && sequence.isSelectionDeletable();

  if (!writable && !deletable) {
    if (sequence.hasNullPhosphate()) {
      notifications.warn('Cannot insert after a null phosphate', {
        id: 'PolymerMapTabView.insertAfterNullPhosphate',
      });
    } else if (sequence.getSequencePartsInSelection().length > 0) {
      notifications.error('Cannot paste while selection spans a part.', {
        id: 'PolymerMapUtilTabView.paste',
      });
    } else if (
      BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS &&
      sequence.getTranscriptionsInSelection().length > 0
    ) {
      notifications.warn('Cannot paste while selection spans a transcription.', {
        id: 'PolymerMapTabView.paste',
      });
    } else {
      checkSoftInvariant(false, {
        message: 'Unexpected unwritable sequence',
        expirationDate: '2025-09-16',
        errorInDevAndTest: true,
      });
    }
    return;
  }

  if (selection.isNotEmpty()) {
    start = selection.start();
    const end = selection.end();
    if (!asm.of(_sequence).canReplace(_sequence, selection, bases, notifications.error)) {
      return;
    }
    deferred = sequence.replace(start, end, bases, {
      parts,
      annotations,
      translations,
      translationGroups,
      sequencePrimers,
      modifiedNucleotides,
    });

    if (sequence.circular() && end <= start) {
      pos = 0;
    } else {
      pos = start;
    }
  } else {
    pos = selection.start();
    deferred = sequence.insert(pos, bases, {
      parts,
      annotations,
      translations,
      translationGroups,
      sequencePrimers,
      modifiedNucleotides,
    });
  }

  // Handle xhr success/failure
  deferred.fail(notifications.notifyError);
  deferred.client.done(() => {
    selection.setEndpoints(pos, pos + bases.length);
  });

  // TODO: Show a loading screen while assembly update is taking place to be "atomic"
  if (asm.of(_sequence).assemblyType != null) {
    deferred.done(() => {
      selection.setEndpoints(pos, pos + bases.length); // Reset the selection in case the user moved it.
      // Note it's important to do this after updating the selection so that the handler gets
      // the new selection.
      if (selection.isNotEmpty()) {
        asm.of(_sequence).didReplace(_sequence, selection, bases, notifications.success, notifications.error);
      } else {
        asm
          .of(_sequence)
          .didInsert(_sequence, selection.start(), bases, notifications.success, notifications.error);
      }
    });
  }
}

function was(count: number): string {
  return pluralize(count, 'was', 'were');
}

export function sanitizeParts(
  sequence: ComponentSequence,
  selection: Selection,
  parts: Array<PartAttributes>,
  targetNucleotideType: NucleotideType,
  sourceNucleotideType: NucleotideType
): Array<PartAttributes> {
  const originalPartsCount = parts.length;
  const wasDisplay = was(originalPartsCount);
  if (sourceNucleotideType !== targetNucleotideType && originalPartsCount > 0) {
    parts = [];
    notifications.warn(
      `${originalPartsCount} of the pasted parts ${wasDisplay} of nucleotide type ${sourceNucleotideType} and so ${wasDisplay} ignored.`
    );
  }

  if (sequence.circular() && sequence.length() === selection.length()) {
    return parts;
  }

  const nonCrossOriginParts = parts.filter((part) => part.start < part.end || part.end === 0);
  const crossOriginPartCount = parts.length - nonCrossOriginParts.length;
  if (crossOriginPartCount > 0) {
    parts = nonCrossOriginParts;
    notifications.warn(
      `${crossOriginPartCount} cross-origin ${pluralize(crossOriginPartCount, 'part')} ${was(
        crossOriginPartCount
      )} ignored.`
    );
  }

  return parts;
}

export enum SanitizeFeatureType {
  ANNOTATIONS = 'annotations',
  SEQUENCE_PRIMERS = 'sequence primers',
  TRANSLATIONS = 'translations',
}

export function sanitizeFeatures<FeatureAttributes extends PolymerFeatureAttributes>({
  sequence,
  selection,
  features,
  featureType,
  rejectReverseStrand,
}: {
  sequence: ComponentSequence;
  selection: Selection;
  features: Array<FeatureAttributes>;
  featureType: SanitizeFeatureType;
  rejectReverseStrand: boolean;
}): Array<FeatureAttributes> {
  // eslint-disable-next-line no-restricted-syntax
  features = _.reject(features, (f) => Boolean(f.fromSubPart));

  if (rejectReverseStrand) {
    const originalFeaturesCount = features.length;
    features = features.filter((f) => f.strand !== -1);

    const numDropped = originalFeaturesCount - features.length;
    if (numDropped > 0) {
      const wasDisplay = was(numDropped);
      notifications.warn(
        `${numDropped} of the pasted ${featureType} ${wasDisplay} on the reverse strand and so ${wasDisplay} ignored.`
      );
    }
  }

  // If we are replacing all the bases, all of the features are okay,
  // unless we are pasting into a linear sequence.
  const fullReplace = selection.length() === sequence.length();
  const onLinear = !sequence.circular();
  if (fullReplace && !onLinear) {
    return features;
  }

  // Split wrapping features into two IF we aren't replacing all the bases.
  const toAdd = [];
  if (featureType !== SanitizeFeatureType.SEQUENCE_PRIMERS) {
    for (const feature of features) {
      if (feature.start >= feature.end && feature.end !== 0) {
        const second = _.clone(feature);
        second.start = 0;
        toAdd.push(second);
        feature.end = 0;
      }
    }
  }
  features.push(...toAdd);
  if (toAdd.length) {
    const was = toAdd.length === 1 ? 'was' : 'were';
    if (fullReplace) {
      notifications.warn(
        <>
          {toAdd.length} of the pasted {featureType} {was} split in two. If the sequence is a plasmid, change
          the topology to <strong>circular</strong> in the Information Panel.
        </>,
        {duration: 6}
      );
    } else {
      notifications.warn(`${toAdd.length} of the pasted ${featureType} ${was} split in two.`);
    }
  }
  return features;
}

function handleCtrlShiftC(sequence: ComponentSequence, e: Event): void {
  // Show the copy menu
  e.preventDefault();
  if (sequence.selection().length() > 0) {
    sequence.handleCopy(
      sequence.nucleotideType() === NucleotideType.DNA
        ? CopyNucleotideSequenceMode.DNA_REVERSE_COMPLEMENT
        : CopyNucleotideSequenceMode.RNA_REVERSE_COMPLEMENT
    );
  }
}

export interface ShowInputBubbleOptions {
  // Which element to refocus to after closing the input bubble
  refocusTo?: HTMLElement;
  // Text to display for paste. Must be defined for a paste operation
  pasteText?: string;
  // Function called when confirming a paste. Must be defined for a paste operation
  doPaste?: () => void;
  // Which operation the input bubble performs.
  // If not defined, will be inferred as a REPLACE or INSERT based on whether the selection is empty or not.
  operation?: INPUT_BUBBLE_OPERATION;
}

function showInputBubble<MapTabState extends PolymerMapTabState<ComponentSequence>>(
  mapView: PolymerMapTabView<MapTabState>,
  param?: ShowInputBubbleOptions
): void {
  if (param == null) {
    param = {};
  }

  const sequence = mapView.mapstate.get('sequence');
  const _sequence = sequence.get('_sequence');
  const selection = sequence.get('selection');

  if (!asm.of(_sequence).canEditSelection(_sequence, selection, notifications.warn)) {
    return;
  }

  let operation = param.operation;
  if (operation == null) {
    operation = selection.length() > 0 ? INPUT_BUBBLE_OPERATION.REPLACE : INPUT_BUBBLE_OPERATION.INSERT;
  }

  const pos = mapView._calculateBubblePos();
  if (pos != null) {
    const {top, left, offset, isPositionedAbove} = pos;

    if (mapView.inputBubbleView != null) {
      mapView.stopListening(mapView.inputBubbleView);
    }

    mapView.inputBubbleView = new InputBubbleView({
      // @ts-expect-error: these parameters are expected in the InputBubbleView initialize method.
      pos: selection.start(),
      model: sequence,
      top,
      left,
      isPositionedAbove,
      offset,
      operation,
      refocusTo: param.refocusTo,
      pasteText: param.pasteText,
      doPaste: param.doPaste,
    });

    mapView.listenTo(mapView.inputBubbleView, 'remove', () => {
      mapView.stopListening(mapView.inputBubbleView);
      mapView.inputBubbleView = null;
    });

    mapView.$el.append(mapView.inputBubbleView.render().el);
  }
}

export default {
  aminoAcidBoxHeight,
  addPolymersForSearch,
  removePolymersForSearch,
  maybeStoreNucleotideRegionInBenchlingClipboard,
  storeNucleotideRegionInBenchlingClipboard,
  handlePasteText,
  handleCtrlShiftC,
  showInputBubble,
};
